import { useContext, useEffect, useState } from 'react';
import useSelectFetchMostRecentBookingLocationDetails from '~/components/AccountGetsMarried/hooks/useSelectFetchMostRecentBookingLocationDetails';
import useStateFromPosition from '~/components/BookingFlow/hooks/useStateFromPosition';
import { MEMBERSHIP_SUPPORTED_STATES } from '~/components/Membership/constants';
import { analyticsTrackEvent } from '~/core/analytics';
import { SOLV_PLUS_ENTRYPOINT_VISIBILITY } from '~/core/analytics/events';
import { UserProfile } from '~/core/dapi/models';
import AuthenticationContext from '~/core/login/AuthenticationContext';
import { getAssociatedTelemedLocationId } from '~/core/util/location';
import { useSolvSelector } from '~/reducers';
import { AccountSummary } from '~/reducers/account';
import { checkMembershipEntrypointFeatureFlag } from '../../config';
import { useAccount } from '../account/useAccount';
import useSelectActiveUserProfile from '../useSelectActiveUserProfile';

type MembershipHookProps = {
  /** Should users who have booked with a location that offers telemed see this entrypoint */
  usersWithClinicRelationshipShouldSeeThis?: boolean;

  /** If a user has already signed up for the membership, should they still see this entrypoint? */
  existingMembershipUsersShouldSeeThis?: boolean;

  /** Entrypoint ID to associate with tracking data */
  entrypointId?: string;
};

type CheckMembershipEntrypointParams = MembershipHookProps & {
  /** Are we loading account data? */
  accountIsLoading?: boolean;

  /** Are we logged in? */
  isLoggedIn?: boolean;

  /** Is the membership feature flag enabled for this enviornment? */
  membershipFeatureFlag?: boolean;

  /** Are we in a supported state */
  inSupportedState?: boolean;

  /** Does the user have a membership already */
  hasMembership?: boolean;

  /** Does the user have a relationship with a clinic that offers telemed */
  hasTelemedClinicRelationship?: boolean;
};

const BLACKLISTED_PARTNER_IDS = [
  // MultiCare
  'VLZqp5',

  // MedStar
  'PWvOZQ',
  'VLlmyW',
  'aLOwvL',

  // Wellstreet
  '4pxJAW',
  'VLRBbL',

  // PM Pediatrics
  'aLO0OW',

  // Springifled Urgent Care
  'PWvRKW',

  // FIdelity Prmier Ohio
  'aLO8qW',
];

/**
 * Handles the flow-chart logic as to whether the entrypoint should be rendered.
 *
 * Derived from: https://lucid.app/lucidchart/invitations/accept/inv_27fb5dc1-4c90-4a20-bf4a-dbe55cf3e827
 *
 * @returns boolean
 */
export function shouldShowMembershipEntrypoint({
  usersWithClinicRelationshipShouldSeeThis,
  existingMembershipUsersShouldSeeThis,
  accountIsLoading,
  isLoggedIn,
  membershipFeatureFlag,
  inSupportedState,
  hasTelemedClinicRelationship,
  hasMembership,
}: CheckMembershipEntrypointParams): boolean {
  // Default to not showing while loading to avoid flashing
  if (accountIsLoading) return false;

  // If we are not logged in, only care about location and feature flag
  if (!isLoggedIn) {
    return !!membershipFeatureFlag && !!inSupportedState;
  }

  // If we have a membership, consumer has control of whether we see this endpoint
  if (hasMembership) {
    return !!existingMembershipUsersShouldSeeThis;
  }

  // Logged in and no membership means we need Feature Flag and Supported State and Either no clinic relationship
  // or to be in an entrypoint where we don't care about clinic relationsihp
  return (
    !!membershipFeatureFlag &&
    !!inSupportedState &&
    (!hasTelemedClinicRelationship || !!usersWithClinicRelationshipShouldSeeThis)
  );
}

/**
 * Determine whether a membership entrypoint should be shown to the user.
 *
 * @returns A boolean
 */
function useShouldShowMembershipEntrypoints({
  usersWithClinicRelationshipShouldSeeThis,
  existingMembershipUsersShouldSeeThis,
  entrypointId,
}: MembershipHookProps) {
  const { isLoggedIn } = useContext(AuthenticationContext);

  const account: AccountSummary | undefined = useAccount();
  const userProfile: UserProfile | undefined = useSelectActiveUserProfile();
  const { location } = useSelectFetchMostRecentBookingLocationDetails(userProfile?.user_profile_id);

  const hasTelemedClinicRelationship =
    location.isTelemed ||
    getAssociatedTelemedLocationId(location) ||
    BLACKLISTED_PARTNER_IDS.includes(location.id);

  const membershipFeatureFlag = checkMembershipEntrypointFeatureFlag();

  const accountIsLoading = useSolvSelector((state) => state.account.accountSummaryLoading);

  const stateFromProfile = userProfile?.address_state;
  const stateFromPosition = useStateFromPosition();
  const state = stateFromProfile ?? stateFromPosition;

  // Default to true if we don't know where the user is
  const inSupportedState = state
    ? MEMBERSHIP_SUPPORTED_STATES.includes(stateFromProfile ?? '') ||
      MEMBERSHIP_SUPPORTED_STATES.includes(stateFromPosition ?? '')
    : true;

  const hasMembership = account?.is_active_membership;

  const [showEntrypoint, setShowEntrypoint] = useState(false);

  useEffect(() => {
    analyticsTrackEvent(SOLV_PLUS_ENTRYPOINT_VISIBILITY, {
      visible: showEntrypoint,
      entrypoint: entrypointId,
    });
  }, [entrypointId, showEntrypoint]);

  useEffect(() => {
    setShowEntrypoint(
      shouldShowMembershipEntrypoint({
        usersWithClinicRelationshipShouldSeeThis,
        existingMembershipUsersShouldSeeThis,
        accountIsLoading,
        isLoggedIn,
        hasMembership,
        membershipFeatureFlag,
        hasTelemedClinicRelationship,
        inSupportedState,
      })
    );
  }, [
    accountIsLoading,
    existingMembershipUsersShouldSeeThis,
    hasMembership,
    hasTelemedClinicRelationship,
    inSupportedState,
    isLoggedIn,
    membershipFeatureFlag,
    usersWithClinicRelationshipShouldSeeThis,
  ]);

  return showEntrypoint;
}

export default useShouldShowMembershipEntrypoints;
